import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useQuery } from '@/api/query'
import { useCurrentUser } from '@/auth/context'
import { displayName, greeting } from '@/lib/format'
import { useActiveTenant } from '@/tenant/context'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { moduleById } from '@/shell/navigation'
import type { ModuleId } from '@/shell/navigation'
import { AccountStatus } from './AccountStatus'

/**
 * The landing screen for a module.
 *
 * S-D3 draws four stat cards and a rank chart here. Every one of those numbers
 * comes from a module that has not been built — GSC clicks are S-B22, published
 * counts are S-B28, the approval queue is S-B27 — so this screen shows what is
 * actually true today: who you are, what organisation you are in, and the fact
 * that the module has nothing running yet.
 *
 * That last part is an **empty state**, not a placeholder, and it is doing the
 * job S-D1 defined for one: "never a bare 'no data' — it says what would be here
 * and how to get it". The module sessions replace it with their own content;
 * nothing here has to be unpicked first.
 */
export function ModuleOverview({ module }: { module: ModuleId }): JSX.Element {
  const definition = moduleById(module)
  const { user } = useCurrentUser()
  const { tenant, role } = useActiveTenant()

  const members = useQuery(
    (signal) =>
      call(() =>
        api.GET('/api/v1/tenants/{tenant_id}/members', {
          params: { path: { tenant_id: tenant.id } },
          signal,
        }),
      ),
    [tenant.id],
  )

  return (
    <>
      <div className="ar-row">
        <h1 className="ar-h1">
          {greeting(new Date(), tenant.timezone)}, {displayName(user.email, user.mobile ?? null)}
        </h1>
        <span className="app-spacer" />
        <span className="ar-badge ar-badge--neutral">{definition.label}</span>
      </div>

      <AccountStatus />

      <div className="ar-grid ar-grid--4">
        <Stat label="Your role" value={role} />
        <Stat label="Organisation" value={tenant.name} />
        <Stat label="Time zone" value={tenant.timezone} />
        {members.state.status === 'ready' ? (
          <Stat
            label="Team"
            value={String(members.state.data.members.length)}
            note={
              members.state.data.seats.limit === null
                ? 'no seat limit'
                : `of ${String(members.state.data.seats.limit)} seats`
            }
          />
        ) : members.state.status === 'error' ? (
          <Stat label="Team" value="—" note="could not be read" />
        ) : (
          <div className="ar-card">
            <div className="ar-card__body">
              <div className="ar-skeleton ar-skeleton--text" style={{ width: '60%' }} />
            </div>
          </div>
        )}
      </div>

      {members.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={members.state.failure}
            title="Could not load your team"
            onRetry={members.refetch}
          />
        </div>
      ) : null}

      {members.state.status === 'loading' ? <Loading label="Loading your organisation" /> : null}

      <div className="ar-card">
        <Empty
          icon={module === 'seo' ? '↗' : '✉'}
          title={`${definition.label} has nothing running yet`}
          body={
            module === 'seo'
              ? 'Connect a site and Search Console, and audits, keyword tracking and drafts start appearing here. Nothing publishes without your approval.'
              : 'Connect LinkedIn and a sender address, and lead discovery, the inbox and the pipeline start filling this screen.'
          }
          action={
            <Link className="ar-btn ar-btn--ghost" to="/settings/organisation">
              Open settings
            </Link>
          }
        />
      </div>
    </>
  )
}

function Stat({
  label,
  value,
  note,
}: {
  label: string
  value: string
  note?: string
}): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__body">
        <div className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          {label}
        </div>
        <div className="ar-h3" style={{ marginTop: 'var(--ar-space-2)' }}>
          {value}
        </div>
        {note ? (
          <div className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
            {note}
          </div>
        ) : null}
      </div>
    </div>
  )
}
